// ═══════════════════════════════════════════════════════════════════════════════
// IndexedDB-backed offline queue (localforage)
// ═══════════════════════════════════════════════════════════════════════════════

import localforage from 'localforage';
import type { SubmitTaskInput } from '@fdh/shared';
import type { OfflineQueueItem, ReceiptOfflinePayload } from './offlineQueueTypes';

const QUEUE_KEY = 'fdh_offline_queue_v1';

const store = localforage.createInstance({
  name: 'fdh_app',
  storeName: 'offline_sync',
});

const readQueue = async (): Promise<OfflineQueueItem[]> => {
  const raw = await store.getItem<string>(QUEUE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as OfflineQueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = async (items: OfflineQueueItem[]): Promise<void> => {
  await store.setItem(QUEUE_KEY, JSON.stringify(items));
};

export const getOfflineQueue = (): Promise<OfflineQueueItem[]> => readQueue();

export const getOfflineQueueLength = async (): Promise<number> => {
  const q = await readQueue();
  return q.length;
};

export const enqueueTaskSubmit = async (input: {
  taskInstanceId: string;
  payload: SubmitTaskInput;
}): Promise<void> => {
  const q = await readQueue();
  const item: OfflineQueueItem = {
    id: crypto.randomUUID(),
    kind: 'task_submit',
    taskInstanceId: input.taskInstanceId,
    payload: input.payload,
    createdAt: new Date().toISOString(),
  };
  q.push(item);
  await writeQueue(q);
};

export const enqueueTaskResubmit = async (input: {
  taskInstanceId: string;
  payload: SubmitTaskInput;
}): Promise<void> => {
  const q = await readQueue();
  const item: OfflineQueueItem = {
    id: crypto.randomUUID(),
    kind: 'task_resubmit',
    taskInstanceId: input.taskInstanceId,
    payload: input.payload,
    createdAt: new Date().toISOString(),
  };
  q.push(item);
  await writeQueue(q);
};

export const enqueueReceiptCreate = async (input: {
  payload: ReceiptOfflinePayload;
  originalImageUrlOrData: string;
}): Promise<void> => {
  const q = await readQueue();
  const item: OfflineQueueItem = {
    id: crypto.randomUUID(),
    kind: 'receipt_create',
    payload: input.payload,
    originalImageUrlOrData: input.originalImageUrlOrData,
    createdAt: new Date().toISOString(),
  };
  q.push(item);
  await writeQueue(q);
};

export const removeQueueItemById = async (id: string): Promise<void> => {
  const q = await readQueue();
  await writeQueue(q.filter((x) => x.id !== id));
};
